import { useEffect, useId, type KeyboardEvent, type ReactNode } from "react";
import { useDraggable } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { useListReorder, usePrefersReducedMotion, type DragItem } from "@/features/dnd/DeskDnd";
import { cn } from "@/lib/utils";
import type { TaskWithFlags } from "@desk/contracts";
import type { TaskGroup } from "@/concepts/types";
import { useConcept } from "@/features/concept-provider";
import { locationLabel, useHierarchy } from "@/data/hooks";
import { LocationIcon } from "@/components/container-icon";
import { useOverlay } from "@/data/outbox";
import { useTaskActions } from "@/data/actions";
import { openTask, useUi } from "@/features/ui-state";
import { modifierSelect, selectSingle } from "@/features/selection/selection";
import { TaskMenu } from "@/features/task/TaskMenu";
import { useTaskDetailParts } from "@/features/task/detail-parts";
import { TaskContextMenu } from "@/features/task/TaskContextMenu";
import { RowQuickActions } from "@/features/task/RowQuickActions";
import { rowChipEditors } from "@/features/task/RowChipEditors";

import { taskRegistry } from "./registry";
import { RowMotion, useCompletionLinger, useNewRows } from "./row-motion";
export { taskRegistry };

export function InlineDetail({ task }: { task: TaskWithFlags }) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const a = useTaskActions();
  const parts = useTaskDetailParts(task);
  if (!parts) return null;
  return (
    <concept.TaskDetail
      task={task}
      mode="inline"
      today={h.today}
      timezone={h.timezone}
      onClose={() => openTask(null)}
      onToggleComplete={() => void a.toggleComplete(task)}
      parts={parts}
    />
  );
}

const SORT_TRANSITION = { duration: 180, easing: "cubic-bezier(0.2, 0, 0, 1)" };

/**
 * A reorderable row. The pointer can drag from anywhere on the row; the keyboard uses the named grip
 * (focus it, Space to pick up, arrows to move, Space or Enter to drop, Escape to cancel). The row
 * left behind stays in place as a dashed placeholder while the lifted copy follows the pointer.
 */
function SortableItem({ task, listId, children }: { task: TaskWithFlags; listId: string; children: ReactNode }) {
  const reduced = usePrefersReducedMotion();
  const data: DragItem = { type: "task", listId, task };
  const s = useSortable({ id: task.id, data, transition: reduced ? null : SORT_TRANSITION });
  const { onKeyDown, ...pointer } = (s.listeners ?? {}) as Record<string, (e: KeyboardEvent<HTMLElement>) => void>;
  return (
    <div
      ref={s.setNodeRef}
      style={{ transform: CSS.Translate.toString(s.transform), transition: s.transition ?? undefined }}
      className={cn("group/drag relative", s.isDragging && "desk-drag-placeholder")}
      {...pointer}
    >
      <button
        type="button"
        ref={s.setActivatorNodeRef}
        {...s.attributes}
        onKeyDown={onKeyDown}
        aria-label={`Reorder ${task.title}`}
        data-drag-handle
        className="absolute -left-6 top-[9px] z-[1] inline-flex size-6 cursor-grab items-center justify-center rounded text-subtle opacity-0 outline-none transition-opacity hover:text-foreground focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/40 active:cursor-grabbing group-hover/drag:opacity-100"
      >
        <GripVertical className="size-3.5" aria-hidden />
      </button>
      {children}
    </div>
  );
}

/** A row that can be dragged to the sidebar or a board, but isn't reordered in place. */
function DraggableItem({ task, listId, children }: { task: TaskWithFlags; listId: string; children: ReactNode }) {
  const data: DragItem = { type: "task", listId, task };
  const d = useDraggable({ id: `${listId}:${task.id}`, data });
  const { onKeyDown: _k, ...pointer } = (d.listeners ?? {}) as Record<string, unknown>;
  void _k;
  return (
    <div ref={d.setNodeRef} className={cn("relative", d.isDragging && "desk-drag-placeholder")} {...pointer}>
      {children}
    </div>
  );
}

export interface TaskListViewProps {
  groups: TaskGroup[];
  ariaLabel: string;
  /** Location label for a row, or null when the row is inside the current container. */
  location?: (t: TaskWithFlags) => string | null;
  empty?: ReactNode;
  footer?: ReactNode;
  trailing?: (t: TaskWithFlags) => ReactNode;
  carriedFrom?: (t: TaskWithFlags) => string | null;
  /** Enables drag reordering inside each group (manual order). */
  onReorder?: (ids: string[], groupKey: string) => void;
  /** Hide the "on Today" star (the Today screen itself). */
  hideTodayStar?: boolean;
  /** Hover quick actions (Today, Snooze) on each open row. Default on. */
  quickActions?: boolean;
}

export function TaskListView({
  groups: listGroups,
  ariaLabel,
  location,
  empty,
  footer,
  trailing,
  carriedFrom,
  onReorder,
  hideTodayStar,
  quickActions = true,
}: TaskListViewProps) {
  const { concept } = useConcept();
  const h = useHierarchy();
  const overlay = useOverlay();
  const a = useTaskActions();
  const openId = useUi((s) => s.openTaskId);
  const selectedId = useUi((s) => s.selectedTaskId);
  const selectedIds = useUi((s) => s.selectedIds);
  const listId = useId();
  const linger = useCompletionLinger(listGroups);
  const groups = linger.groups;
  const isNew = useNewRows(listGroups);

  useEffect(() => {
    const ids: string[] = [];
    groups.forEach((g) => g.tasks.forEach((t) => (taskRegistry.set(t.id, t), ids.push(t.id))));
    return () => ids.forEach((id) => taskRegistry.delete(id));
  }, [groups]);

  const locate = location ?? ((t: TaskWithFlags) => locationLabel(t, h));

  const renderTask = (task: TaskWithFlags) => {
    const expanded = openId === task.id;
    if (expanded && concept.detailMode === "inline") return (
      <div key={task.id} id={`task-${task.id}`} data-task-id={task.id}>
        <InlineDetail task={task} />
      </div>
    );
    const row = (
      <concept.TaskRow
        key={task.id}
        task={task}
        selected={selectedId === task.id || selectedIds.includes(task.id)}
        expanded={expanded}
        sync={overlay.marks.get(task.id) ?? null}
        location={locate(task)}
        locationIcon={<LocationIcon task={task} className="size-3.5 text-[8px]" />}
        today={h.today}
        timezone={h.timezone}
        plannedToday={!hideTodayStar && task.plannedDate === h.today}
        carriedFrom={carriedFrom?.(task) ?? null}
        onToggleComplete={() => {
          if (task.status !== "done" && task.status !== "cancelled") linger.onCompleted(task);
          void a.toggleComplete(task);
        }}
        onOpen={() => openTask(expanded ? null : task.id)}
        onSelect={() => selectSingle(task.id)}
        menu={<TaskMenu task={task} />}
        trailing={trailing?.(task)}
        quickActions={quickActions ? <RowQuickActions task={task} /> : null}
        chipEditors={rowChipEditors(task)}
        rowProps={{
          id: `task-${task.id}`,
          role: "option",
          "aria-selected": selectedId === task.id || selectedIds.includes(task.id),
          "data-task-id": task.id,
          tabIndex: -1,
          // ⌘/Ctrl-click toggles and ⇧-click extends the selection instead of opening the task.
          onClickCapture: (e) => {
            if (modifierSelect(task.id, e)) {
              e.preventDefault();
              e.stopPropagation();
            }
          },
          onMouseDownCapture: (e) => {
            if (e.shiftKey) e.preventDefault();
          },
          onKeyDown: (e) => {
            if (e.key === "Enter" && e.target === e.currentTarget) openTask(task.id);
          },
        }}
      />
    );
    const withMenu = (
      <TaskContextMenu key={task.id} task={task}>
        {row}
      </TaskContextMenu>
    );
    const item = onReorder ? (
      <SortableItem key={task.id} task={task} listId={listId}>
        {withMenu}
      </SortableItem>
    ) : (
      <DraggableItem key={task.id} task={task} listId={listId}>
        {withMenu}
      </DraggableItem>
    );
    return (
      <RowMotion key={task.id} leaving={linger.leaving(task.id)} entering={isNew(task.id)}>
        {item}
      </RowMotion>
    );
  };

  const list = (
    <concept.TaskList
      groups={groups}
      renderTask={renderTask}
      empty={empty}
      footer={footer}
      listProps={{
        role: "listbox",
        "aria-label": ariaLabel,
        "aria-activedescendant": selectedId ? `task-${selectedId}` : undefined,
        "aria-multiselectable": true,
      }}
    />
  );

  // Applied when a row is dropped on another row of the same list; groups reorder independently.
  useListReorder(
    listId,
    onReorder &&
      ((activeId: string, overId: string) => {
        const group = groups.find((g) => g.tasks.some((t) => t.id === activeId));
        if (!group || !group.tasks.some((t) => t.id === overId)) return;
        const ids = group.tasks.map((t) => t.id);
        onReorder(arrayMove(ids, ids.indexOf(activeId), ids.indexOf(overId)), group.key);
      }),
  );

  if (!onReorder) return list;
  return (
    <SortableContext id={listId} items={groups.flatMap((g) => g.tasks.map((t) => t.id))} strategy={verticalListSortingStrategy}>
      {list}
    </SortableContext>
  );
}
